import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const emailResetSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type EmailResetForm = z.infer<typeof emailResetSchema>;

export default function ForgotPasswordPage() {
  const [isEmailSent, setIsEmailSent] = useState(false);

  const form = useForm<EmailResetForm>({
    resolver: zodResolver(emailResetSchema),
    defaultValues: {
      email: '',
    },
  });

  const sendEmailResetMutation = useMutation({
    mutationFn: async (data: EmailResetForm) => {
      if (!supabase) {
        throw new Error('Supabase client not available');
      }
      
      console.log('[FORGOT PASSWORD] Sending reset link to:', data.email);
      
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        data.email,
        {
          redirectTo: `${window.location.origin}/reset-password`
        }
      );

      if (resetError) {
        console.error('[FORGOT PASSWORD] Reset error:', resetError);
        throw resetError;
      }
      
      console.log('[FORGOT PASSWORD] Reset link sent successfully');
      return;
    },
    onSuccess: () => {
      setIsEmailSent(true);
    },
  });

  const onSubmit = async (data: EmailResetForm) => {
    await sendEmailResetMutation.mutateAsync(data);
  };

  // Email sent confirmation page
  if (isEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-xl font-semibold">Check Your Email</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-gray-600 dark:text-gray-400">
              We've sent you a password reset link. Click the link in your email to reset your password.
            </p>
            <p className="text-center text-sm text-gray-500 dark:text-gray-500">
              The link will expire in 1 hour. Check your spam folder if you don't see the email.
            </p>
            <div className="pt-4 space-y-2">
              <Button 
                onClick={() => setIsEmailSent(false)} 
                variant="outline" 
                className="w-full"
                data-testid="button-send-another-link"
              >
                Send Another Link
              </Button>
              <Link href="/auth?tab=login" data-testid="link-back-to-login">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main reset page
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Reset Your Password</CardTitle>
          <p className="text-gray-600 dark:text-gray-400">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter your email"
                        disabled={sendEmailResetMutation.isPending}
                        data-testid="input-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {sendEmailResetMutation.error && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {sendEmailResetMutation.error instanceof Error 
                      ? sendEmailResetMutation.error.message 
                      : 'Failed to send reset link. Please try again.'}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={sendEmailResetMutation.isPending}
                data-testid="button-send-reset-link"
              >
                {sendEmailResetMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center">
            <Link href="/auth?tab=login" data-testid="link-back-to-login-bottom">
              <Button variant="ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
